import { MONTHS } from '../constants';

/**
 * Calculate days until a date (negative if past)
 */
export function daysUntil(dateStr: string): number {
  const date = new Date(dateStr);
  date.setHours(0, 0, 0, 0);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return Math.round((date.getTime() - now.getTime()) / 86400000);
}

/**
 * Format minutes to human-readable duration (e.g., "1h30" or "45min")
 */
export function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours > 0) {
    return `${hours}h${mins > 0 ? String(mins).padStart(2, '0') : ''}`;
  }
  return `${mins}min`;
}

/**
 * Get today's date in ISO format (YYYY-MM-DD)
 */
export function today(): string {
  return new Date().toISOString().split('T')[0];
}

/**
 * Get color for urgency (red if overdue/within 7 days, orange if within 30 days, green otherwise)
 */
export function urgencyColor(daysLeft: number): string {
  if (daysLeft < 0 || daysLeft <= 7) return '#E24B4A'; // red
  if (daysLeft <= 30) return '#BA7517'; // orange
  return '#3B6D11'; // green
}

/**
 * Get human-readable urgency label
 */
export function urgencyLabel(daysLeft: number): string {
  if (daysLeft < 0) return `Dépassé ${Math.abs(daysLeft)}j`;
  if (daysLeft === 0) return "Aujourd'hui";
  if (daysLeft === 1) return 'Demain';
  return `Dans ${daysLeft}j`;
}

/**
 * Get the Monday of the week containing the given date
 */
export function getMondayOf(date: Date | string): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * Add days to a date
 */
export function addDays(date: Date | string, n: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

/**
 * Convert date to ISO format string
 */
export function toISODate(date: Date): string {
  return date.toISOString().split('T')[0];
}

/**
 * Format date in French (e.g., "19 avril 2026")
 */
export function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return `${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
}

/**
 * Format date and time in French (e.g., "19/04/2026 à 14:30")
 */
export function formatDateTime(iso: string): string {
  const date = new Date(iso);
  return `${date.toLocaleDateString('fr-FR')} à ${date.toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
}

/**
 * Get a date N days from now in ISO format
 */
export function makeDateFrom(daysOffset: number): string {
  const d = new Date();
  d.setDate(d.getDate() + daysOffset);
  return d.toISOString().split('T')[0];
}

/**
 * Generate a unique ID
 */
export function generateId(prefix = 'id'): string {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Deep clone an object
 */
export function deepClone<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}

/**
 * Group items by a key function
 */
export function groupBy<T>(items: T[], keyFn: (item: T) => string): Record<string, T[]> {
  return items.reduce((acc, item) => {
    const key = keyFn(item);
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {} as Record<string, T[]>);
}